var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

import { TernaryPlot } from './ternary';
import { bwr } from '../utils/colors/presets';
import { DataProvider } from '../data-provider';
import { select } from 'd3-selection';

var QuaternaryPlot =
/** @class */
function () {
  function QuaternaryPlot(svg, dp, edgeUnit) {
    if (edgeUnit === void 0) {
      edgeUnit = 250;
    }

    this.shells = [];
    this.nShells = 0;

    this.onSelect = function () {};

    this.onDeselect = function () {};

    this.svg = svg;
    this.dp = dp;
    this.edgeUnit = edgeUnit;
  }

  QuaternaryPlot.prototype.initShells = function (spacing) {
    // Initialize 4 ternary plots per shell
    var nShells = Math.floor(1 / (3 * spacing));

    if (nShells === this.nShells) {
      return;
    }

    this.nShells = nShells;
    var nPlots = 4;
    this.shells = [];
    var margin = 50;
    var startX = 0;
    var rowStartY = 0;
    var rowHeight = Math.round(this.edgeUnit * Math.sin(2 * Math.PI / 6));
    var maxWidth = 1200;
    var sizeX = 0;
    var sizeY = rowHeight;
    select(this.svg).selectAll().remove();

    for (var i = 0; i < nShells; ++i) {
      var constValue = i * spacing;
      var edge = Math.round(this.edgeUnit * (1 - constValue * 4));

      if (edge <= 0) {
        continue;
      }

      var plots = [];
      var shellWidth = 2.5 * edge;
      var shellHeight = Math.round(edge * Math.sin(2 * Math.PI / 6));

      if (startX + shellWidth > maxWidth) {
        startX = 0;
        rowStartY += rowHeight + 2 * margin;
        rowHeight = shellHeight;
        sizeY += 2 * margin + rowHeight;
      }

      sizeX = Math.max(sizeX, startX + shellWidth);
      var startY = margin + rowStartY + Math.floor(0.5 * (rowHeight - shellHeight));

      for (var j = 0; j < nPlots; ++j) {
        var dp = new DataProvider(3);
        var plot = new TernaryPlot(this.svg, dp, "face" + i + "-" + j, j % 2 !== 0);
        var left = margin + startX + j * (edge / 2);
        plot.setPosition(left, startY, edge);
        plots.push(plot);
      }

      startX += 2 * edge + 2 * spacing * this.edgeUnit;
      this.shells.push(plots);
    }

    sizeX += 2 * margin;
    sizeY += 2 * margin;
    this.svg.setAttribute('width', sizeX.toString());
    this.svg.setAttribute('height', sizeY.toString());
    this.setCallBacks(this.onSelect, this.onDeselect);
  };

  QuaternaryPlot.prototype.dataUpdated = function () {
    var spacing = this.dp.getAxis(0).spacing;
    this.initShells(spacing);
    this.setShellsData(spacing);
    this.render();
  };

  QuaternaryPlot.prototype.setColorMap = function (map, range) {
    var _this = this;

    if (range === void 0) {
      range = null;
    }

    if (!map) {
      map = bwr;
    }

    if (!range) {
      range = this.dp.getScalarRange(this.selectedScalar);
    }

    var scalar = this.dp.getActiveScalar();
    this.colorMapRange = range;
    this.colorMap = map;

    var fn = function fn(plot) {
      plot.dp.setActiveScalar(plot.dp.getDefaultScalar(scalar));
      plot.setColorMap(_this.colorMap, _this.colorMapRange);
    };

    this.broadCast(fn);
  };

  QuaternaryPlot.prototype.setInverted = function (inverted) {
    var fn = function fn(plot) {
      plot.setInverted(inverted);
    };

    this.broadCast(fn);
  };

  QuaternaryPlot.prototype.setShellsData = function (spacing) {
    for (var i = 0; i < this.shells.length; ++i) {
      var constValue = i * spacing;

      this._setShellData(this.shells[i], constValue);
    }
  };

  QuaternaryPlot.prototype.setCallBacks = function (onSelect, onDeselect) {
    this.onSelect = onSelect;
    this.onDeselect = onDeselect;

    var fn = function fn(plot) {
      plot.onSelect = onSelect;
      plot.onDeselect = onDeselect;
    };

    this.broadCast(fn);
  };

  QuaternaryPlot.prototype.setSelectedSamples = function (selectedSamples) {
    var _this = this;

    this.selectedSamples = selectedSamples;

    var fn = function fn(plot) {
      plot.setSelectedSamples(_this.selectedSamples);
    };

    this.broadCast(fn);
    this.render();
  };

  QuaternaryPlot.prototype.setOpacityFn = function (opacityFn) {
    var fn = function fn(plot) {
      plot.setOpacityFn(opacityFn);
    };

    this.broadCast(fn);
    this.render();
  };

  QuaternaryPlot.prototype.render = function () {
    var fn = function fn(plot) {
      plot.render();
    };

    this.broadCast(fn);
  };

  QuaternaryPlot.prototype._setShellData = function (plots, constValue) {
    var _a;

    var axes = this.dp.getAxes();
    var allAxes = this.dp.getAxes(true);
    var activeAxes = new Set(this.dp.getActiveAxes());
    var filters = Object.values(allAxes).map(function (axis) {
      if (activeAxes.has(axis.element)) {
        return __assign({}, axis, {
          range: [constValue, 1]
        });
      } else {
        return __assign({}, axis, {
          range: function range(val, eps) {
            return val < eps;
          }
        });
      }
    });

    var _b = Object.keys(axes),
        Aidx = _b[0],
        Bidx = _b[1],
        Cidx = _b[2],
        Didx = _b[3];

    var shellData = this.dp.slice(filters);
    var permutations = [[Aidx, Bidx, Cidx, Didx], [Cidx, Didx, Bidx, Aidx], [Bidx, Aidx, Didx, Cidx], [Didx, Cidx, Aidx, Bidx]];

    for (var i = 0; i < permutations.length; ++i) {
      var perm = permutations[i];
      var filters_1 = [{
        element: perm[3],
        range: [constValue, constValue]
      }];

      if (i > 0) {
        filters_1.push({
          element: perm[1],
          range: function range(val, eps) {
            return Math.abs(val - constValue) > eps;
          }
        });
      }

      var slicedSamples = DataProvider.filterSamples(shellData, filters_1);
      plots[i].dp.setData(slicedSamples, false);
      plots[i].dp.setAxisOrder(perm);
      var newAxes = (_a = {}, _a[perm[0]] = __assign({}, axes[perm[0]], {
        range: [constValue, 1 - 3 * constValue]
      }), _a[perm[1]] = __assign({}, axes[perm[1]], {
        range: [constValue, 1 - 3 * constValue]
      }), _a[perm[2]] = __assign({}, axes[perm[2]], {
        range: [constValue, 1 - 3 * constValue]
      }), _a[perm[3]] = __assign({}, axes[perm[3]], {
        range: [constValue, constValue]
      }), _a);
      plots[i].dp.setAxes(newAxes);
      plots[i].dp.setActiveScalar(this.dp.getActiveScalar());
      plots[i].dataUpdated();
    }
  }; // Helper function to broadcast an action to all the the ternary plots composing the
  // quaternary plot


  QuaternaryPlot.prototype.broadCast = function (fn) {
    for (var _i = 0, _a = this.shells; _i < _a.length; _i++) {
      var shell = _a[_i];

      for (var _b = 0, shell_1 = shell; _b < shell_1.length; _b++) {
        var plot = shell_1[_b];
        fn(plot);
      }
    }
  };

  return QuaternaryPlot;
}();

export { QuaternaryPlot };